// All derived numbers, as pure functions over one assessment's dataset
// (slice 1 spec R10, R18; slice 4 §07, U8, U9, U18, U19, U21, U22, U23).
// Shared by the server (QC, note bases) and web/ (every Teaching screen),
// so both always compute the same thing. No imports: it compiles under the
// server's NodeNext config and web's bundler config alike.
// Missing evidence (null) and not-yet-marked answers are excluded from
// every denominator and never count as a 0.

export type ClassFilter = string; // class name at sitting, or 'all'
export type Status = 'completed' | 'absent' | 'catchup' | 'exempt';

/** The fields of a dataset the analysis reads (server getDataset and the
 *  web client's Dataset both satisfy it). */
export interface AnalysisDataset {
  assessment: { id: string; title: string; year_level: number; criterion: string; sat_on: string | null; extension_item_id: string | null };
  items: { id: string; label: string; prompt: string; max_mark: number; threshold: number; topic: string; strand: string; command_term: string }[];
  misconceptions: { id: string; code: string; meaning: string; short_label: string; item_ids: string[] }[];
  students: { student_id: string; name: string; class_name_at_sitting: string; attendance: 'present' | 'absent' | 'catchup' | 'exempt'; identity_status: string; revision: number }[];
  results: { student_id: string; item_id: string; mark: number | null; unavailable_reason: string | null; blank: boolean; evidence: string | null; codes: string[] }[];
  judgements: { student_id: string; teacher_level: number | null; teacher_note: string | null; status: string; disposition?: string; disposition_reason?: string | null }[];
  feedback: { student_id: string; strength: string | null; improvement: string | null; next_step: string | null; target: string | null; status: string; not_applicable_reason?: string | null }[];
  classes: string[];
  /** U22: teacher-set progression steps; absent or empty = none set. */
  progression?: { item_id: string; step: number }[];
}

export interface Item {
  id: string;        // label, e.g. "Q1"; unique within the assessment
  uuid: string;
  prompt: string;
  max: number;
  threshold: number;
  topic: string;
  strand: string;
  command: string;
  codes: string[];   // misconception codes this item can evidence
}

export interface Feedback { strength: string; improvement: string; nextStep: string; target: string; needsReview?: boolean; draft?: boolean }

export interface Student {
  id: string;
  name: string;
  cls: string;
  status: Status;
  verified: boolean;
  revision: number;
  /** Teacher-confirmed level (needsReview: evidence changed since). Drafts are not levels. */
  myp?: { level: number; note: string; needsReview: boolean };
  /** A teacher-saved level not yet confirmed. */
  mypDraft?: number;
  /** U4: the teacher recorded "not assessable"; never a level, never 0. */
  notAssessable?: { reason: string; needsReview: boolean };
  feedback?: Feedback;
  /** U16: the teacher marked feedback not applicable, with this reason. */
  feedbackNotApplicable?: string;
}

export interface ItemResult {
  studentId: string;
  itemId: string;
  /** null = no valid evidence or not marked yet. A genuine 0 is 0. */
  mark: number | null;
  unavailableReason?: 'unreadable' | 'page missing';
  notMarked?: boolean;
  blank?: boolean;
  misconceptions: string[];
  evidence?: string;
}

/** n = answers with valid evidence; items and students = distinct items and
 *  students behind those answers (U8, U21). */
export interface Rate { mastered: number; n: number; rate: number | null; items: number; students: number }
const mkRate = (m: number, n: number, items: number, students: number): Rate => ({ mastered: m, n, rate: n > 0 ? m / n : null, items, students });

export interface StudentSummary {
  student: Student;
  results: ItemResult[];
  validCount: number;
  masteredCount: number;
  /** V4: sum of approved marks; null when nothing is evidenced (absent etc.). */
  marks: number | null;
  /** V4: maximum over the evidenced items; null when nothing is evidenced. */
  maxOfValid: number | null;
  attainment: number | null;
  complete: boolean;
  fullyMarked: boolean;
  topics: Record<string, Rate>;
  codes: string[];
  eligible: boolean;
  reasons: string[];
  flagged: boolean;
}

const cap = (x: string) => x ? x[0].toUpperCase() + x.slice(1) : x;

export type GroupKind = 'reteach' | 'small' | 'individual' | 'extension';
export interface Group {
  kind: GroupKind; cls: string; title: string; reason: string; students: Student[];
  /** Small groups: the misconception code or topic the group is built on. */
  code?: string; topic?: string;
  /** V5: per-student reason where it differs from the group's (partial coverage). */
  studentReasons?: Record<string, string>;
}

export interface MisconceptionRow {
  code: string;
  meaning: string;
  students: { s: Student; itemId: string; evidence?: string }[];
}

export const WEAK = 0.5;
export const MIN_CLASS_N = 10;
export const MIN_TOPIC_ITEMS = 3;
/** U8: a weakness claim needs at least this many distinct evidenced items. */
export const MIN_BUCKET_ITEMS = 2;
/** U21: a class-wide claim needs this many evidenced students, and half the class. */
export const MIN_CLASS_STUDENTS = 5;

/** U18: three states. Insufficient coverage is never "weak" nor "not weak". */
export type WeakState = 'not_enough_evidence' | 'weak' | 'not_weak';

export function weakState(r: Rate): WeakState {
  if (r.rate === null || r.n < MIN_CLASS_N || r.items < MIN_BUCKET_ITEMS) return 'not_enough_evidence';
  return r.rate < WEAK ? 'weak' : 'not_weak';
}

/** null = not enough evidence (U8, U18). */
export function isWeak(r: Rate): boolean | null {
  const s = weakState(r);
  return s === 'not_enough_evidence' ? null : s === 'weak';
}

/** U21: class-level weak (and whole-class reteach) also needs at least 5
 *  evidenced students and at least half of the present students. */
export function classWeakState(r: Rate, presentStudents: number): WeakState {
  const s = weakState(r);
  if (s === 'not_enough_evidence') return s;
  if (r.students < MIN_CLASS_STUDENTS || r.students < presentStudents * 0.5) return 'not_enough_evidence';
  return s;
}

// ── V3: one level distribution for Overview, Progress and the export ──

export const LEVEL_BUCKETS = ['0', '1-2', '3-4', '5-6', '7-8', 'not_assessable', 'not_yet_confirmed'] as const;
export type LevelBucket = (typeof LEVEL_BUCKETS)[number];
export type LevelStatus = 'confirmed' | 'needs_review' | 'not_assessable' | 'not_yet_confirmed' | 'absent' | 'catchup' | 'exempt';
export interface LevelDistribution { buckets: Record<LevelBucket, number>; population: number }

/** Only a confirmed level reaches a numeric bucket; needs_review (an
 *  invalidated judgement) and drafts are "not yet confirmed". Students who
 *  did not sit are outside the population. */
export function levelDistribution(points: { status: LevelStatus; level: number | null }[]): LevelDistribution {
  const buckets = Object.fromEntries(LEVEL_BUCKETS.map((b) => [b, 0])) as Record<LevelBucket, number>;
  let population = 0;
  for (const p of points) {
    if (p.status === 'absent' || p.status === 'catchup' || p.status === 'exempt') continue;
    population++;
    if (p.status === 'not_assessable') buckets.not_assessable++;
    else if (p.status !== 'confirmed' || p.level === null) buckets.not_yet_confirmed++;
    else { const l = p.level; buckets[l === 0 ? '0' : l <= 2 ? '1-2' : l <= 4 ? '3-4' : l <= 6 ? '5-6' : '7-8']++; }
  }
  return { buckets, population };
}

/** The level status of one analysed student (same rules as progress pointOf). */
export function levelStatusOf(s: Student): { status: LevelStatus; level: number | null } {
  if (s.status !== 'completed') return { status: s.status === 'catchup' ? 'catchup' : s.status, level: null };
  if (s.myp) return s.myp.needsReview ? { status: 'needs_review', level: null } : { status: 'confirmed', level: s.myp.level };
  if (s.notAssessable) return { status: s.notAssessable.needsReview ? 'needs_review' : 'not_assessable', level: null };
  return { status: 'not_yet_confirmed', level: null };
}

export type AnalysisTab = 'item' | 'strand' | 'topic' | 'command';
export const ANALYSIS_TABS: AnalysisTab[] = ['item', 'strand', 'topic', 'command'];
export type BucketKey = 'topic' | 'strand' | 'command';

export interface ItemTableRow {
  item: Item; valid: number; meanMark: number | null; max: number; success: number | null; r: Rate;
  topCodes: { code: string; count: number }[]; rank: 'strongest' | 'weakest' | null;
}
export interface BucketRow {
  label: string; r: Rate; items: string[]; weak: WeakState;
  /** Students under 50% in this bucket with at least 2 evidenced items in it. */
  under50: Student[];
}
export interface Diagnostic { bucket: string; limitations: { code: string; meaning: string; count: number }[]; zeroMarkCommand: { command: string; count: number } | null }
export interface TabResult<R> {
  /** U9: NOT APPLICABLE only when no row has evidence. */
  applicable: boolean; reason: string | null;
  /** Strongest/weakest lines: "not applicable: one category" with one row. */
  comparative: boolean; comparativeReason: string | null;
  rows: R[];
  diagnostics: Diagnostic[];
}
export interface ProgressionResult {
  applicable: boolean; reason: string | null;
  steps: { step: number; items: string[] }[];
  foundational: string[];
  barrier: { step: number; items: string[]; failed: number; of: number } | null;
  unusual: { student: Student; masteredStep: number; failedLower: string[] }[];
}

export type Analysis = ReturnType<typeof analyse>;

export function analyse(ds: AnalysisDataset) {
  const codeById = new Map(ds.misconceptions.map((m) => [m.id, m]));
  const items: Item[] = ds.items.map((i) => ({
    id: i.label, uuid: i.id, prompt: i.prompt, max: i.max_mark, threshold: i.threshold,
    topic: i.topic || 'Untagged', strand: i.strand || 'Untagged', command: i.command_term || 'Untagged',
    codes: ds.misconceptions.filter((m) => m.item_ids.includes(i.id)).map((m) => m.code),
  }));
  const itemByUuid = new Map(items.map((i) => [i.uuid, i]));
  const itemById: Record<string, Item> = Object.fromEntries(items.map((i) => [i.id, i]));
  const meaning: Record<string, string> = Object.fromEntries(ds.misconceptions.map((m) => [m.code, m.meaning]));
  const short: Record<string, string> = Object.fromEntries(ds.misconceptions.map((m) => [m.code, m.short_label || m.meaning]));
  const classes = ds.classes;
  const total = items.reduce((s, i) => s + i.max, 0);

  const judgement = new Map(ds.judgements.map((j) => [j.student_id, j]));
  const fb = new Map(ds.feedback.map((f) => [f.student_id, f]));
  const students: Student[] = ds.students.map((e) => {
    const j = judgement.get(e.student_id);
    const f = fb.get(e.student_id);
    const na = j?.disposition === 'not_assessable' && j.status !== 'draft';
    return {
      id: e.student_id, name: e.name, cls: e.class_name_at_sitting,
      status: e.attendance === 'present' ? 'completed' : e.attendance,
      verified: e.identity_status === 'verified', revision: e.revision,
      myp: j && !na && j.teacher_level !== null && j.status !== 'draft' ? { level: j.teacher_level, note: j.teacher_note ?? '', needsReview: j.status === 'needs_review' } : undefined,
      mypDraft: j && j.teacher_level !== null && j.status === 'draft' ? j.teacher_level : undefined,
      notAssessable: na ? { reason: j!.disposition_reason ?? '', needsReview: j!.status === 'needs_review' } : undefined,
      feedback: f && f.status !== 'not_applicable' ? { strength: f.strength ?? '', improvement: f.improvement ?? '', nextStep: f.next_step ?? '', target: f.target ?? '',
        needsReview: f.status === 'needs_review', draft: f.status === 'draft' } : undefined,
      feedbackNotApplicable: f && f.status === 'not_applicable' ? f.not_applicable_reason ?? '' : undefined,
    };
  });
  const studentById: Record<string, Student> = Object.fromEntries(students.map((s) => [s.id, s]));

  const byStudent = new Map<string, Map<string, AnalysisDataset['results'][number]>>();
  for (const r of ds.results) {
    const m = byStudent.get(r.student_id) ?? new Map();
    m.set(r.item_id, r);
    byStudent.set(r.student_id, m);
  }

  function resultsFor(s: Student): ItemResult[] {
    if (s.status !== 'completed') return [];
    const rows = byStudent.get(s.id);
    return items.map((it) => {
      const r = rows?.get(it.uuid);
      if (!r) return { studentId: s.id, itemId: it.id, mark: null, notMarked: true, misconceptions: [] };
      return {
        studentId: s.id, itemId: it.id, mark: r.mark,
        unavailableReason: (r.unavailable_reason ?? undefined) as ItemResult['unavailableReason'],
        blank: r.blank, evidence: r.evidence ?? undefined,
        misconceptions: r.codes.map((c) => codeById.get(c)?.code).filter((c): c is string => !!c),
      };
    });
  }

  function mastered(res: ItemResult | undefined): boolean | null {
    if (!res || res.mark === null) return null;
    return res.mark >= itemById[res.itemId].threshold;
  }

  const topicItemCount = new Map<string, number>();
  for (const it of items) topicItemCount.set(it.topic, (topicItemCount.get(it.topic) ?? 0) + 1);
  const minEvidence = Math.ceil(items.length * 0.8);
  const extensionItem = (ds.assessment.extension_item_id && itemByUuid.get(ds.assessment.extension_item_id))
    || [...items].sort((a, b) => b.max - a.max)[0];

  function summarise(student: Student): StudentSummary {
    const results = resultsFor(student);
    const valid = results.filter((x) => x.mark !== null);
    const masteredCount = valid.filter((x) => mastered(x)).length;
    const marks = valid.reduce((s, x) => s + (x.mark ?? 0), 0);
    const maxOfValid = valid.reduce((s, x) => s + itemById[x.itemId].max, 0);

    const topics: Record<string, Rate> = {};
    for (const res of results) {
      const it = itemById[res.itemId];
      const t = topics[it.topic] ?? mkRate(0, 0, 0, 0);
      if (res.mark !== null) { t.n += 1; t.items += 1; t.students = 1; if (mastered(res)) t.mastered += 1; }
      t.rate = t.n > 0 ? t.mastered / t.n : null;
      topics[it.topic] = t;
    }

    const codes = Array.from(new Set(results.flatMap((x) => x.misconceptions)));
    const eligible = student.status === 'completed' && items.length > 0 && valid.length >= minEvidence;
    const reasons: string[] = [];
    if (eligible) {
      if (codes.length >= 2) reasons.push(codes.map((c) => `${meaning[c] || cap(short[c])} [${c}]`).join(' · '));
      for (const [topic, t] of Object.entries(topics)) {
        if ((topicItemCount.get(topic) ?? 0) >= MIN_TOPIC_ITEMS && t.n >= MIN_TOPIC_ITEMS && (t.rate ?? 1) < WEAK) {
          reasons.push(`Weak on ${topic.toLowerCase()}: ${t.mastered} of ${t.n} questions`);
        }
      }
      if (masteredCount < valid.length * 0.5) reasons.push(`Mastered only ${masteredCount} of ${valid.length} questions`);
    }
    return {
      student, results, validCount: valid.length, masteredCount,
      marks: valid.length ? marks : null, maxOfValid: valid.length ? maxOfValid : null,
      attainment: maxOfValid > 0 ? marks / maxOfValid : null,
      complete: student.status === 'completed' && valid.length === items.length,
      fullyMarked: student.status === 'completed' && results.every((r) => !r.notMarked),
      topics, codes, eligible, reasons: reasons.slice(0, 3), flagged: reasons.length > 0,
    };
  }

  const summaries = new Map(students.map((s) => [s.id, summarise(s)]));
  const studentsIn = (cls: ClassFilter) => students.filter((s) => cls === 'all' || s.cls === cls);
  const summariesIn = (cls: ClassFilter) => studentsIn(cls).map((s) => summaries.get(s.id)!);
  const presentIn = (cls: ClassFilter) => studentsIn(cls).filter((s) => s.status === 'completed').length;

  function struggling(cls: ClassFilter): StudentSummary[] {
    return summariesIn(cls).filter((s) => s.eligible && s.flagged).sort((a, b) =>
      a.masteredCount - b.masteredCount || (a.attainment ?? 0) - (b.attainment ?? 0) || a.student.name.localeCompare(b.student.name));
  }

  /** Mastery over a set of items for the students in `cls`. */
  function rateOver(its: Item[], cls: ClassFilter): Rate {
    let m = 0, n = 0;
    const evItems = new Set<string>(); const evStudents = new Set<string>();
    for (const s of summariesIn(cls)) {
      for (const item of its) {
        const res = s.results.find((x) => x.itemId === item.id);
        if (!res || res.mark === null) continue;
        n++; evItems.add(item.id); evStudents.add(s.student.id);
        if (mastered(res)) m++;
      }
    }
    return mkRate(m, n, evItems.size, evStudents.size);
  }

  function itemRate(item: Item, cls: ClassFilter): Rate { return rateOver([item], cls); }

  function bucketLabels(key: BucketKey): string[] {
    return Array.from(new Set(items.map((i) => i[key])));
  }

  function bucketRates(key: BucketKey, cls: ClassFilter): { label: string; r: Rate }[] {
    return bucketLabels(key).map((label) => ({ label, r: rateOver(items.filter((i) => i[key] === label), cls) }));
  }

  function misconceptionRows(cls: ClassFilter): MisconceptionRow[] {
    const map = new Map<string, MisconceptionRow>();
    for (const s of summariesIn(cls)) {
      for (const res of s.results) {
        for (const code of res.misconceptions) {
          const row = map.get(code) ?? { code, meaning: meaning[code], students: [] };
          if (!row.students.some((x) => x.s.id === s.student.id)) row.students.push({ s: s.student, itemId: res.itemId, evidence: res.evidence });
          map.set(code, row);
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => b.students.length - a.students.length || a.code.localeCompare(b.code));
  }

  /** V5: honest about coverage. */
  function extensionReason(s: StudentSummary): string {
    return s.validCount < items.length ? `Mastered every evidenced question (${s.validCount} of ${items.length} evidenced)` : 'Mastered every question';
  }

  function groupsForClass(cls: string): Group[] {
    const out: Group[] = [];
    const sums = summariesIn(cls);
    const present = presentIn(cls);
    for (const { label, r } of bucketRates('topic', cls)) {
      // U8, U18, U21: whole-class reteach only on a sufficiently evidenced weakness.
      if (classWeakState(r, present) === 'weak') {
        out.push({ kind: 'reteach', cls, title: `Reteach ${label.toLowerCase()} to all of ${cls}`,
          reason: `Only ${Math.round((r.rate ?? 0) * 100)}% mastery across ${r.n} answers`,
          students: sums.filter((s) => s.student.status === 'completed').map((s) => s.student) });
      }
    }
    const inSmall = new Set<string>();
    for (const code of Object.keys(meaning)) {
      const members = sums.filter((s) => s.eligible && s.codes.includes(code)).map((s) => s.student);
      if (members.length >= 3) {
        members.forEach((m) => inSmall.add(m.id));
        out.push({ kind: 'small', cls, title: `${cap(short[code])}, ${cls}`, code, reason: meaning[code], students: members });
      }
    }
    for (const topic of topicItemCount.keys()) {
      const members = sums.filter((s) => s.eligible && (s.topics[topic]?.n ?? 0) >= 2 && (s.topics[topic].rate ?? 1) < WEAK).map((s) => s.student);
      if (members.length >= 3) {
        members.forEach((m) => inSmall.add(m.id));
        out.push({ kind: 'small', cls, title: `Weak on ${topic.toLowerCase()}, ${cls}`, topic, reason: `Under 50% mastery on ${topic.toLowerCase()}`, students: members });
      }
    }
    const individual = sums.filter((s) => s.eligible && s.flagged && !inSmall.has(s.student.id));
    if (individual.length) out.push({ kind: 'individual', cls, title: `One-to-one in ${cls}`, reason: 'Unusual pattern not shared with others', students: individual.map((s) => s.student) });

    if (extensionItem) {
      // Flagged students are never also extension (R18).
      const ext = sums.filter((s) => {
        if (!s.eligible || s.flagged || s.masteredCount < s.validCount) return false;
        const r = s.results.find((x) => x.itemId === extensionItem.id);
        return !!r && r.mark !== null && r.mark >= extensionItem.max * 0.8;
      });
      if (ext.length) {
        const on = `${Math.ceil(extensionItem.max * 0.8)}+/${extensionItem.max} on ${extensionItem.id}`;
        // V5: say "every evidenced question" when a member's coverage is incomplete.
        const partial = ext.filter((s) => s.validCount < items.length);
        const studentReasons = Object.fromEntries(partial.map((s) => [s.student.id, `${extensionReason(s)}, with ${on}`]));
        out.push({ kind: 'extension', cls, title: `Extension in ${cls}`,
          reason: partial.length ? `Mastered every evidenced question, with ${on}` : `Mastered every question, with ${on}`, students: ext.map((s) => s.student),
          ...(partial.length ? { studentReasons } : {}) });
      }
    }
    return out;
  }

  const groupsByClass = new Map(classes.map((c) => [c, groupsForClass(c)]));
  const groupsIn = (cls: ClassFilter): Group[] => cls === 'all' ? Array.from(groupsByClass.values()).flat() : groupsByClass.get(cls) ?? [];

  /** §05/T7: approved feedback, a draft (labelled "Draft, not approved"),
   *  or nothing. Never generated text. */
  function feedbackFor(sum: StudentSummary): Feedback | null {
    if (sum.student.status !== 'completed') return null;
    return sum.student.feedback ?? null;
  }

  const present = students.filter((s) => s.status === 'completed');
  const approval = { fully: present.filter((s) => summaries.get(s.id)!.fullyMarked).length, of: present.length };

  // ── §07, U9, U19: analysis tables ────────────────────────────────────

  /** Comparative lines need at least two rows with evidence (U9). */
  function comparative(rows: number): Pick<TabResult<unknown>, 'comparative' | 'comparativeReason'> {
    return rows >= 2 ? { comparative: true, comparativeReason: null } : { comparative: false, comparativeReason: 'not applicable: one category' };
  }

  function codeCounts(its: Item[], cls: ClassFilter): { code: string; count: number }[] {
    const ids = new Set(its.map((i) => i.id));
    const counts = new Map<string, number>();
    for (const s of summariesIn(cls)) for (const res of s.results) if (ids.has(res.itemId)) for (const c of res.misconceptions) counts.set(c, (counts.get(c) ?? 0) + 1);
    return Array.from(counts, ([code, count]) => ({ code, count })).sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
  }

  function zeroMarkCommand(its: Item[], cls: ClassFilter): { command: string; count: number } | null {
    const ids = new Set(its.map((i) => i.id));
    const counts = new Map<string, number>();
    for (const s of summariesIn(cls)) for (const res of s.results) if (ids.has(res.itemId) && res.mark === 0) {
      const c = itemById[res.itemId].command;
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    const top = Array.from(counts, ([command, count]) => ({ command, count })).sort((a, b) => b.count - a.count || a.command.localeCompare(b.command))[0];
    return top ?? null;
  }

  function diagnostic(label: string, its: Item[], cls: ClassFilter): Diagnostic {
    return {
      bucket: label,
      limitations: codeCounts(its, cls).slice(0, 3).map((c) => ({ ...c, meaning: meaning[c.code] ?? '' })),
      zeroMarkCommand: zeroMarkCommand(its, cls),
    };
  }

  function itemTable(cls: ClassFilter): TabResult<ItemTableRow> {
    const rows: ItemTableRow[] = items.map((item) => {
      const r = itemRate(item, cls);
      let sum = 0;
      for (const s of summariesIn(cls)) { const res = s.results.find((x) => x.itemId === item.id); if (res && res.mark !== null) sum += res.mark; }
      const meanMark = r.n > 0 ? sum / r.n : null;
      return { item, valid: r.n, meanMark, max: item.max, success: meanMark === null ? null : meanMark / item.max, r, topCodes: codeCounts([item], cls).slice(0, 3), rank: null };
    });
    rows.sort((a, b) => (b.r.rate ?? -1) - (a.r.rate ?? -1) || a.item.id.localeCompare(b.item.id, undefined, { numeric: true }));
    const rated = rows.filter((x) => x.r.rate !== null);
    const k = Math.min(3, Math.floor(rated.length / 2));
    rated.slice(0, k).forEach((x) => { x.rank = 'strongest'; });
    rated.slice(rated.length - k).forEach((x) => { x.rank = 'weakest'; });
    // One item is its own bucket: weak with n >= 10 and under 50%.
    const weakItems = rows.filter((x) => x.r.rate !== null && x.r.n >= MIN_CLASS_N && x.r.rate < WEAK);
    return {
      applicable: rated.length > 0, reason: rated.length ? null : 'Not available for this test yet: no question has marks to look at',
      ...comparative(rated.length), rows,
      diagnostics: weakItems.map((x) => diagnostic(x.item.id, [x.item], cls)),
    };
  }

  function bucketTable(key: BucketKey, cls: ClassFilter): TabResult<BucketRow> {
    const presentN = presentIn(cls);
    const rows: BucketRow[] = bucketLabels(key).map((label) => {
      const its = items.filter((i) => i[key] === label);
      const r = rateOver(its, cls);
      const under50 = summariesIn(cls).filter((s) => {
        const ev = s.results.filter((x) => its.some((i) => i.id === x.itemId) && x.mark !== null);
        return ev.length >= 2 && ev.filter((x) => mastered(x)).length / ev.length < WEAK;
      }).map((s) => s.student);
      return { label, r, items: its.map((i) => i.id), weak: classWeakState(r, presentN), under50 };
    });
    const withEvidence = rows.filter((x) => x.r.n > 0);
    return {
      applicable: withEvidence.length > 0, reason: withEvidence.length ? null : 'Not available for this test yet: no category has marks to look at',
      ...comparative(withEvidence.length), rows,
      diagnostics: rows.filter((x) => x.weak === 'weak').map((x) => diagnostic(x.label, items.filter((i) => i[key] === x.label), cls)),
    };
  }

  function tab(t: AnalysisTab, cls: ClassFilter): TabResult<ItemTableRow | BucketRow> {
    return t === 'item' ? itemTable(cls) : bucketTable(t, cls);
  }

  /** U23: a stable fingerprint of what a tab computes for the whole test. */
  function tabDigest(t: AnalysisTab): string {
    const res = tab(t, 'all');
    const rows = res.rows.map((x) => 'item' in x
      ? [x.item.uuid, x.r.mastered, x.r.n, x.r.items, x.r.students, x.meanMark === null ? null : Math.round(x.meanMark * 1000), x.rank, x.topCodes]
      : [x.label, x.r.mastered, x.r.n, x.r.items, x.r.students, x.weak, x.under50.map((s) => s.id).sort()]);
    return hashString(JSON.stringify([t, res.applicable, res.comparative, rows, res.diagnostics]));
  }

  // ── §37, U19, U22: Guttman and progression ───────────────────────────

  function guttman(cls: ClassFilter): { applicable: boolean; reason: string | null } {
    if (items.length < 4) return { applicable: false, reason: 'Not available: this test has fewer than 4 questions' };
    const rates = new Set(items.map((i) => itemRate(i, cls).rate).filter((r): r is number => r !== null).map((r) => Math.round(r * 1e6)));
    if (rates.size < 2) return { applicable: false, reason: 'Not available: every question has the same mastery rate' };
    return { applicable: true, reason: null };
  }

  const stepOf = new Map((ds.progression ?? []).map((p) => [p.item_id, p.step]));

  function progression(cls: ClassFilter): ProgressionResult {
    const none: ProgressionResult = { applicable: false, reason: 'Not available: no question order has been set for this test', steps: [], foundational: [], barrier: null, unusual: [] };
    const stepped = items.filter((i) => stepOf.has(i.uuid));
    if (!stepped.length) return none;
    const stepNums = Array.from(new Set(stepped.map((i) => stepOf.get(i.uuid)!))).sort((a, b) => a - b);
    const steps = stepNums.map((step) => ({ step, items: stepped.filter((i) => stepOf.get(i.uuid) === step).map((i) => i.id) }));
    const sums = summariesIn(cls).filter((s) => s.student.status === 'completed');
    const res = (s: StudentSummary, id: string) => s.results.find((x) => x.itemId === id);
    /** true mastered all, false failed (evidence, not all mastered), null no full evidence. */
    const stepState = (s: StudentSummary, ids: string[]): boolean | null => {
      const rs = ids.map((id) => res(s, id));
      if (rs.some((r) => !r || r.mark === null)) return null;
      return rs.every((r) => mastered(r));
    };
    let barrier: ProgressionResult['barrier'] = null;
    for (const [k, st] of steps.entries()) {
      const lower = steps.slice(0, k).flatMap((x) => x.items);
      const pool = sums.filter((s) => lower.every((id) => mastered(res(s, id)) === true));
      const judged = pool.filter((s) => stepState(s, st.items) !== null);
      const failed = judged.filter((s) => stepState(s, st.items) === false).length;
      if (judged.length >= 5 && failed >= judged.length * 0.5) { barrier = { step: st.step, items: st.items, failed, of: judged.length }; break; }
    }
    const unusual: ProgressionResult['unusual'] = [];
    for (const s of sums) {
      for (const [k, st] of steps.entries()) {
        if (k === 0 || stepState(s, st.items) !== true) continue;
        const failedLower = steps.slice(0, k).flatMap((x) => x.items).filter((id) => mastered(res(s, id)) === false);
        if (failedLower.length >= 2) { unusual.push({ student: s.student, masteredStep: st.step, failedLower }); break; }
      }
    }
    return { applicable: true, reason: null, steps, foundational: steps[0].items, barrier, unusual };
  }

  return {
    assessment: ds.assessment, items, itemById, classes, total, meaning, short, students, studentById,
    mastered, summaryOf: (id: string) => summaries.get(id), studentsIn, summariesIn, struggling, itemRate,
    bucketRates, misconceptionRows, groupsIn, presentIn,
    groupsForStudent: (id: string) => groupsIn('all').filter((g) => g.students.some((s) => s.id === id)),
    feedbackFor, approval, minEvidence, extensionItem, extensionReason,
    levelDistribution: (cls: ClassFilter) => levelDistribution(studentsIn(cls).map(levelStatusOf)),
    topicRuleApplies: [...topicItemCount.values()].some((n) => n >= MIN_TOPIC_ITEMS),
    itemTable, bucketTable, tab, tabDigest, guttman, progression,
  };
}

/** FNV-1a, two lanes: deterministic, dependency-free, same in node and the browser. */
export function hashString(s: string): string {
  let a = 0x811c9dc5, b = 0x01000193 ^ 0x5bd1e995;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x01000193 + 2) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}
